import { useCallback, useRef, useState, useEffect, ReactNode } from 'react';
import { cn } from '@/lib/utils.js';

interface ResizableSplitProps {
  left: ReactNode;
  right: ReactNode;
  splitRatio?: number;
  onSplitRatioChange?: (ratio: number) => void;
  minRatio?: number;
  maxRatio?: number;
  className?: string;
}

export function ResizableSplit({
  left,
  right,
  splitRatio = 0.5,
  onSplitRatioChange,
  minRatio = 0.25,
  maxRatio = 0.75,
  className,
}: ResizableSplitProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localRatio, setLocalRatio] = useState(splitRatio);

  // Sync with external ratio
  useEffect(() => {
    setLocalRatio(splitRatio);
  }, [splitRatio]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const newRatio = Math.max(minRatio, Math.min(maxRatio, x / rect.width));

      setLocalRatio(newRatio);
      onSplitRatioChange?.(newRatio);
    },
    [isDragging, minRatio, maxRatio, onSplitRatioChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const leftWidth = `${localRatio * 100}%`;
  const rightWidth = `${(1 - localRatio) * 100}%`;

  return (
    <div ref={containerRef} className={cn('flex h-full w-full', className)}>
      {/* Left panel */}
      <div
        className="h-full overflow-hidden flex flex-col"
        style={{ width: leftWidth }}
      >
        {left}
      </div>

      {/* Resizer handle */}
      <div
        className={cn(
          'relative w-1 h-full cursor-col-resize flex-shrink-0 group',
          'hover:bg-primary/20 transition-colors',
          isDragging && 'bg-primary/30'
        )}
        onMouseDown={handleMouseDown}
      >
        {/* Visual indicator */}
        <div
          className={cn(
            'absolute inset-y-0 -left-1 -right-1 flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            isDragging && 'opacity-100'
          )}
        >
          <div className="w-1 h-8 rounded-full bg-primary/50" />
        </div>
      </div>

      {/* Right panel */}
      <div
        className="h-full overflow-hidden flex flex-col"
        style={{ width: `calc(${rightWidth} - 4px)` }}
      >
        {right}
      </div>
    </div>
  );
}
